import React, { useState, useEffect } from 'react';
import './AppointmentDetails.css';
import './AppointmentPremiumDetails.css';

import API_ENDPOINTS from '../../../config/apiEndpoints';
import {
    PhoneCall, Mail, MessageSquare, Mic,
    FileEdit, Upload, ClipboardCheck,
    Calendar, Percent, Settings, FileText,
    CheckCircle, AlertCircle, Info, X, Save, Edit2, Minus,
    CheckCircle2, User, Paperclip, ChevronLeft, ChevronDown
} from 'lucide-react';
import { getAvatarColorScheme } from '../../../utils/helpers';
import Loader from '../../../components/Loader/Loader';
import AppointmentActionMenu from './AppointmentActionMenu';
import LaunchIcon from '@mui/icons-material/Launch';
import { motion, AnimatePresence } from 'framer-motion';
import { getStatusDescriptor, parseCustomDate } from '../../Task/Components/common/premiumUtils';


const ThemedAlert = ({ isOpen, type, title, message, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="themed-alert-overlay" onClick={onClose}>
            <div className="themed-alert-modal" onClick={e => e.stopPropagation()}>
                <div className={`themed-alert-icon ${type}`}>
                    {type === 'success' ? <CheckCircle size={40} color="#28a745" /> : <AlertCircle size={40} color="#dc3545" />}
                </div>
                <h2 className="themed-alert-title">{title}</h2>
                <p className="themed-alert-message">{message}</p>
                <button className={`themed-alert-btn ${type}`} onClick={onClose}>
                    OK
                </button>
            </div>
        </div>
    );
};

const AppointmentDetails = ({ task, onBack, mode: initialMode = 'view', setMode: parentSetMode, isInline = false, onViewLead, onScorecardMouseEnter, onScorecardMouseLeave }) => {
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
    const [themedAlert, setThemedAlert] = useState({
        isOpen: false,
        type: 'success',
        title: '',
        message: ''
    });

    const [taskDetail, setTaskDetail] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [comments, setComments] = useState([]);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [commentFile, setCommentFile] = useState(null);
    const [commentFileName, setCommentFileName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const TOKEN = API_ENDPOINTS.TOKE.UNIVERSAL_TOKEN;
    const NEW_ADDED_BASE_URL = (process.env.REACT_APP_BACKEND_BASEURL_FOR_DEVELOPMENT || '').replace(/\/$/, '');
    const KIT19 = (process.env.REACT_APP_ASSETS_BASE_URL || '').replace(/\/$/, '');
    const NEW_BASE_URL = (process.env.REACT_APP_SERVICES_AZURE_BASEURL || '').replace(/\/$/, '');
    const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');
    const API_BASE_WCF = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
    const GetAppointment_Details = `${NEWV3_BASE_URL}${API_ENDPOINTS.APPOINTMENTS.GET_TASK_DETAILS_BYID}`;
   
    const ADD_COMMENT = `${NEW_BASE_URL}${API_ENDPOINTS.TASKS.ADD_COMMENT}`;
     const GET_TASK_COMMENTS = `${NEW_ADDED_BASE_URL}${API_ENDPOINTS.COMMON.GET_COMMENTS}`;

    const closeThemedAlert = () => setThemedAlert(prev => ({ ...prev, isOpen: false }));
    const showErrorAlert = (message, title = 'Error!') => setThemedAlert({ isOpen: true, type: 'error', title, message });

    const getInitials = (name) => {
        if (!name || name === 'N/A' || name === 'null') return '??';
        const trimmed = name.toString().trim();
        const digitsOnly = trimmed.replace(/\D/g, '');
        if (/^\+?\d+$/.test(trimmed) || (digitsOnly.length > 0 && digitsOnly.length === trimmed.replace(/\s/g, '').length)) {
            return digitsOnly.substring(0, 2);
        }
        const parts = trimmed.split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '??';
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    useEffect(() => {
        const appointmentId = task?.id || task?.Id || task?.TaskHistoryId || task?.taskId;
        if (task && appointmentId) {
            fetchTaskDetailById();
            fetchComments();
        }
    }, [task]);

    const formatDisplayDate = (dateStr) => {
        if (!dateStr || dateStr === '0' || dateStr === 'null' || dateStr === null) return '--';
        const s = String(dateStr).trim();
        if (!s || s.startsWith('1900') || s === '0') return '--';

        const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (s.includes('/Date(')) {
            const ts = parseInt(s.replace(/\/Date\((-?\d+)\)\//, '$1'), 10);
            if (isNaN(ts)) return '--';
            const d = new Date(ts);
            if (isNaN(d.getTime()) || d.getFullYear() < 1971) return '--';
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const mm = String(d.getMinutes()).padStart(2, '0');
            const ss = String(d.getSeconds()).padStart(2, '0');
            return `${dd}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}:${ss}`;
        }

        const d = new Date(s);
        if (isNaN(d.getTime()) || d.getFullYear() < 1971) return '--';
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        return `${dd}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}:${ss}`;
    };

    const resolveDate = (...candidates) => {
        for (const d of candidates) {
            const result = formatDisplayDate(d);
            if (result && result !== '--') return result;
        }
        return '--';
    };

    const getStatusBadge = () => {
        if (!taskDetail && !task) return null;
        
        const status = taskDetail?.taskStatus || taskDetail?.status || task?.taskStatus || task?.status || 'Open';
        const dueDate = taskDetail?.EndDate || task?.endDate;
        const completedDate = taskDetail?.CompletedOn || task?.completedDate;
        const title = taskDetail?.Title || task?.taskTitle || '';
        const startDate = taskDetail?.StartDate || task?.startDate;
        const statusLabel = taskDetail?.statusLabel || task?.statusLabel;
        const outcomeName = taskDetail?.Outcomes?.[0]?.Text || task?.outcomeName;
        const createdDate = taskDetail?.CreatedOn || task?.createdDate;

        const descriptor = getStatusDescriptor(status, dueDate, completedDate, title, startDate, statusLabel, outcomeName, createdDate);
        
        if (!descriptor) return null;

        const isCompleted = descriptor.toLowerCase().includes('completed');
        const isOverdue = descriptor.toLowerCase().includes('overdue');
        const isDueToday = descriptor.toLowerCase().includes('today');

        let badgeClass = 'status-badge-premium pending';
        if (isCompleted) badgeClass = 'status-badge-premium early';
        if (isOverdue) badgeClass = 'status-badge-premium late';
        if (isDueToday) badgeClass = 'status-badge-premium on-time';

        return <div className={badgeClass}>{descriptor.toUpperCase()}</div>;
    };

    const fetchTaskDetailById = async () => {
        const appointmentId = task?.id || task?.Id || task?.TaskHistoryId || task?.taskId;
        if (!appointmentId) return;
        try {
            setIsLoadingDetail(true);
            const userId = localStorage.getItem('userId') || '34594';
            const response = await fetch(GetAppointment_Details, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify({ Token: "-2295521862261168", Details: { Id: parseInt(appointmentId, 10) } })
            });
            if (response.ok) {
                const result = await response.json();
                if (result.d) setTaskDetail(result.d);
            }
        } catch (error) {
            console.error('[AppointmentDetails] Fetch error:', error);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const renderAvatar = (name, url = null, size = 'sm') => {
        const getAvatarSize = (s) => {
            if (s === 'lg') return '60px';
            if (s === 'xs') return '32px';
            return '40px';
        };
        const getFontSize = (s) => {
            if (s === 'lg') return '24px';
            if (s === 'xs') return '12px';
            return '14px';
        };
        const avatarSize = getAvatarSize(size);
        const initials = getInitials(name);
        const { bg, text } = getAvatarColorScheme(name);
        
        const isPlaceholder = !url || url.includes('img_avatar1.png') || url.includes('agent.jpg');

        if (!isPlaceholder) {
            const finalUrl = url.startsWith('http') ? url : `https://kit19.com${url.startsWith('/') ? '' : '/'}${url}`;
            return (
                <div style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bg }}>
                    <img src={finalUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = initials; }} />
                </div>
            );
        }

        return (
            <div className={`avatar-initials-circle ${size}`} style={{
                backgroundColor: bg, position: 'relative', width: avatarSize, height: avatarSize, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: text, fontWeight: 'bold', fontSize: getFontSize(size),
                border: `2px solid #fff`, boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                {initials}
            </div>
        );
    };


    const fetchComments = async () => {
        try {
            setIsLoadingComments(true);
            const appointmentId = task?.id || task?.Id || task?.TaskHistoryId || task?.taskId;
            const response = await fetch(GET_TASK_COMMENTS, {
                method: 'POST',
                headers: { "Content-Type": "application/json; charset=UTF-8" },
                body: JSON.stringify({ 
                    Token: TOKEN || "-2295521862261168",
                    Details: {
                        Id: parseInt(appointmentId, 10),
                        Mode: "A"
                    }
                })
            });
            if (response.ok) {
                const result = await response.json();
                if (result.Details) {
                    setComments(Array.isArray(result.Details) ? result.Details : []);
                }
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return;
        try {
            setIsSubmitting(true);
            const userId = localStorage.getItem('userId') || '34594';
            const appointmentId = task?.id || task?.Id || task?.TaskHistoryId || task?.taskId;
            
            const details = {
                Token: TOKEN || "-2295521862261168",
                taskAttachment: {
                    Remarks: newComment,
                    CreatedBy: parseInt(userId, 10),
                    TaskId: parseInt(appointmentId, 10),
                    ParentId: parseInt(userId, 10),
                    Mode: "C"
                }
            };

            const formData = new FormData();
            formData.append('details', JSON.stringify(details));
            if (commentFile) {
                formData.append('file', commentFile);
            }

            const response = await fetch(ADD_COMMENT, { 
                method: 'POST', 
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData 
            });

            if (response.ok) {
                const result = await response.json();
                if (result.Status === 1 || result.Message === "Success" || result.d === "Success") {
                    setNewComment('');
                    setCommentFile(null);
                    setCommentFileName('');
                    fetchComments();
                } else {
                    showErrorAlert(result.Message || "Failed to add comment");
                }
            } else {
                showErrorAlert("Failed to add comment");
            }
        } catch (error) {
            console.error("Error adding comment:", error);
            showErrorAlert("An error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!task) return null;

    return (
        <div className={`task-details-wrapper ${isInline ? 'is-inline' : ''}`}>
            {!isInline && <div className="task-details-backdrop" onClick={onBack}></div>}
            <div className="task-details-panel">
                {!isInline && (
                    <div className="premium-task-details-header">
                        <button className="task-details-close-btn" onClick={onBack} title="Back">
                            <ChevronLeft size={20} />
                            <span className="back-text">Back to Appointment</span>
                        </button>
                    </div>
                )}

                <div className="premium-details-container">
                    <div className="premium-details-card">
                        <div className="card-body">
                            <div className="card-top-row">
                                <h1 className="premium-task-title">{taskDetail?.Title || task.taskTitle}</h1>
                                {getStatusBadge()}
                            </div>
                            <div className="related-to-premium">
                                <div 
                                    className="related-initials-avatar" 
                                    onMouseEnter={(e) => onScorecardMouseEnter?.(e, taskDetail?.LeadView?.LeadID || task.leadId, taskDetail?.Title || task.taskTitle)} 
                                    onMouseLeave={onScorecardMouseLeave}
                                    style={{
                                        backgroundColor: getAvatarColorScheme(taskDetail?.LeadView?.PersonName || task.name).bg,
                                        color: getAvatarColorScheme(taskDetail?.LeadView?.PersonName || task.name).text
                                    }}
                                >
                                    {getInitials(taskDetail?.LeadView?.PersonName || task.name)}
                                </div>
                                <div className="related-label-box">
                                    <span className="label">Related To:</span>
                                    <span className="value">
                                        {taskDetail?.LeadView?.PersonName || task.name || '-'}
                                    </span>
                                </div>
                            </div>
                            <div className="metadata-grid-premium two-columns">
                                <div className="metadata-column">
                                    {[
                                        { label: 'Due Date:', value: resolveDate(taskDetail?.EndDate, task.endDate) },
                                        { label: 'Completed On:', value: resolveDate(taskDetail?.CompletedOn, task.completedDate) },
                                        { label: 'Collaborators:', value: taskDetail?.TaskCollaborationUsers?.map(u => u.PersonName).join(', ') || 'Mukesh Kumar' }
                                    ].map((meta, i) => (
                                        <div key={i} className="metadata-row">
                                            <span className="metadata-label">{meta.label}</span>
                                            <span className="metadata-value">{meta.value}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="metadata-column">
                                    {[
                                        { label: 'Owner:', value: taskDetail?.OwnerName || task.ownerName || '-' },
                                        { label: 'Outcomes:', value: taskDetail?.Outcomes?.[0]?.Text || task.outcomeName || '--' },
                                        { label: 'Created On:', value: resolveDate(taskDetail?.CreatedOn, task.createdDate) },
                                        (taskDetail?.CreatedByName && taskDetail.CreatedByName !== 'System') ? { label: 'Created By:', value: taskDetail.CreatedByName } : null
                                    ].filter(Boolean).map((meta, i) => (
                                        <div key={i} className="metadata-row">
                                            <span className="metadata-label">{meta.label}</span>
                                            <span className="metadata-value">{meta.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="premium-description-section">
                                <div className="description-premium-area">
                                    <span className="label">Description:</span>
                                    <p className="text">{taskDetail?.Description || task.description || '-'}</p>
                                </div>
                            </div>
                            <hr className="divider" />
                            <div className="comments-section-container">
                                <div className="premium-comments-tab"><h3 className="section-title">Comments</h3></div>
                                <div className="comments-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '10px' }}>
                                    {isLoadingComments ? (<div>Loading...</div>) : comments.filter(c => (c.display_name || 'User') !== 'System').map((comment, idx) => (
                                        <div key={idx} className="comment-item-premium" style={{ display: 'flex', gap: '14px' }}>
                                            <div className="comment-avatar" style={{ flexShrink: 0 }}>
                                                {renderAvatar(comment.display_name || 'User', comment.Image || comment.ProfileImg || comment.ProfileImgFileName, 'xs')}
                                            </div>
                                            <div className="comment-content-box" style={{ flex: 1 }}>
                                                <div className="comment-header" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                                    <span style={{ fontWeight: '600', fontSize: '13.5px', color: '#1e293b' }}>{comment.display_name || 'User'}</span>
                                                    <span style={{ fontWeight: '400', color: '#94a3b8', fontSize: '12px' }}>{comment.created_on}</span>
                                                </div>
                                                <div className="comment-text" style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                                                    {comment.remarks || comment.Remarks || ""}
                                                </div>
                                                {comment.attachments && comment.attachments.length > 0 && (
                                                    <div className="comment-attachments" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                        {comment.attachments.map((file, fIdx) => (
                                                            <a 
                                                                key={fIdx} 
                                                                href={file.FileUrl || file.FileUrl1 || file.FileLocation} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: '#f1f5f9', borderRadius: '6px', color: '#2563eb', fontSize: '12px', textDecoration: 'none', border: '1px solid #e2e8f0' }}
                                                            >
                                                                <Paperclip size={12} />
                                                                <span style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {file.FileName || 'Attachment'}
                                                                </span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="comment-input-area" style={{ marginTop: '15px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
                                        {commentFile && (
                                            <div className="selected-comment-file-chip" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 12px', background: '#f1f5f9', borderRadius: '16px', fontSize: '12px', width: 'fit-content' }}>
                                                <Paperclip size={12} />
                                                <span>{commentFileName}</span>
                                                <X size={14} style={{ cursor: 'pointer' }} onClick={() => { setCommentFile(null); setCommentFileName(""); }} />
                                            </div>
                                        )}
                                        <div className="comment-input-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Enter comment..." 
                                                value={newComment} 
                                                onChange={(e) => setNewComment(e.target.value)} 
                                                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} 
                                                style={{ flex: 1, border: 'none', padding: '4px 8px', outline: 'none' }}
                                            />
                                            <input 
                                                type="file" 
                                                id="appointment-comment-file-input" 
                                                style={{ display: 'none' }} 
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        setCommentFile(file);
                                                        setCommentFileName(file.name);
                                                    }
                                                }}
                                            />
                                            <label htmlFor="appointment-comment-file-input" style={{ cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center' }}>
                                                <Paperclip size={18} style={{ color: '#64748b' }} />
                                            </label>
                                            <button 
                                                onClick={handleAddComment} 
                                                disabled={isSubmitting || (!newComment.trim() && !commentFile)} 
                                                style={{ background: 'none', border: 'none', color: '#28a745', fontWeight: 'bold', padding: '4px 8px', cursor: 'pointer' }}
                                            >
                                                Send
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {themedAlert.isOpen && <ThemedAlert {...themedAlert} onClose={closeThemedAlert} />}
        </div>
    );
};

export default AppointmentDetails;
